/**
 * Feb 2026 R293zm · Tabular line items editor.
 *
 * Replaces the previous card-per-row layout with a dense table so users
 * get an Excel-like multi-row view. Mobile falls back to a stacked card
 * layout (same semantics, below `sm:`). Perf-critical row is memoised
 * so unrelated composer state doesn't repaint every row on each keystroke.
 *
 * Parent owns the `lines` array and passes stable callbacks:
 *   • setLine(idx, patch)  — merge patch into lines[idx]
 *   • addLine()            — append a fresh EMPTY_LINE
 *   • rmLine(idx)          — remove a row
 *   • pickHsn(idx, code)   — hydrate HSN row from catalog
 */
import { memo, useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Package, Maximize2 } from "lucide-react";
import InlineCatalogPicker from "./InlineCatalogPicker";
import LineRichTextDialog from "./LineRichTextDialog";

// Feb 2026 R293zzc · Auto-growing textarea. Mimics an inline Excel
// cell: starts as a single line, grows up to `max` rows with content,
// then scrolls. Keeps the dense grid feel but lets long descriptions
// wrap properly instead of truncating.
function AutoGrowTextarea({ value, onChange, placeholder, onKeyDown, testid, max = 6 }) {
  const sizeFromValue = (v) => {
    const n = (v || "").split("\n").length;
    return Math.min(max, Math.max(1, n));
  };
  return (
    <Textarea
      data-testid={testid}
      placeholder={placeholder}
      className="min-h-[32px] py-1 px-2 text-xs leading-tight resize-none"
      rows={sizeFromValue(value)}
      value={value || ""}
      onChange={onChange}
      onKeyDown={onKeyDown}
    />
  );
}

const _lineAmount = (ln) => {
  const q = Number(ln.qty) || 0;
  const r = Number(ln.rate) || 0;
  const d = Number(ln.discount_pct) || 0;
  return q * r * (1 - d / 100);
};

const TableRow = memo(function TableRow({
  ln, i, gstOn, hsn, setLine, rmLine, pickHsn, canRemove, isLast, onEnterLast, ws, pickItem,
}) {
  const [richOpen, setRichOpen] = useState(false);
  const onDesc = useCallback((e) => setLine(i, { description: e.target.value }), [i, setLine]);
  const onRichSave = useCallback((val) => setLine(i, { description: val }), [i, setLine]);
  const onQty  = useCallback((e) => setLine(i, { qty: e.target.value }), [i, setLine]);
  const onRate = useCallback((e) => setLine(i, { rate: e.target.value }), [i, setLine]);
  const onDisc = useCallback((e) => setLine(i, { discount_pct: e.target.value }), [i, setLine]);
  const onGst  = useCallback((e) => setLine(i, { gst_rate: e.target.value }), [i, setLine]);
  const onHsn  = useCallback((v) => pickHsn(i, v), [i, pickHsn]);
  const onRm   = useCallback(() => rmLine(i), [i, rmLine]);
  const onPickItem = useCallback((item) => pickItem?.(i, item), [i, pickItem]);
  const onLineKey = useCallback((e) => {
    // Feb 2026 R293zzc · Description is now a textarea — plain Enter
    // must insert a newline (multiline support). Only Ctrl/Cmd+Enter
    // triggers "advance to next line" on the last row.
    if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return;
    if (!isLast) return;
    e.preventDefault();
    onEnterLast?.();
  }, [isLast, onEnterLast]);

  const amount = _lineAmount(ln);

  return (
    <tr className="hover:bg-muted/40 border-b border-border last:border-b-0" data-testid={`gst-composer-line-${i}`}>
      <td className="py-1 px-1 text-[11px] text-muted-foreground font-mono text-center w-7">{i + 1}</td>
      {gstOn && (
        <td className="py-1 px-1">
          <Select value={ln.hsn_sac || ""} onValueChange={onHsn}>
            <SelectTrigger className="h-8 text-xs" data-testid={`gst-composer-line-hsn-${i}`}>
              <SelectValue placeholder="HSN" />
            </SelectTrigger>
            <SelectContent>
              {hsn.map((h) => (
                <SelectItem key={h.code} value={h.code}>
                  {h.code} · {h.description} · {h.gst_rate}%
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
      )}
      <td className="py-1 px-1">
        <div className="flex items-start gap-1">
          <div className="flex-1 min-w-0">
            <AutoGrowTextarea
              testid={`gst-composer-line-desc-${i}`}
              placeholder="Description *"
              value={ln.description}
              onChange={onDesc}
              onKeyDown={onLineKey}
            />
          </div>
          <button
            type="button"
            onClick={() => setRichOpen(true)}
            title="Open rich text editor"
            className="h-8 w-7 flex items-center justify-center text-muted-foreground hover:text-foreground"
            data-testid={`gst-composer-line-desc-expand-${i}`}
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
          {ws && pickItem && (
            <InlineCatalogPicker ws={ws} onPick={onPickItem} />
          )}
        </div>
        {richOpen && (
          <LineRichTextDialog
            value={ln.description || ""}
            onSave={(v) => { onRichSave(v); setRichOpen(false); }}
            onClose={() => setRichOpen(false)}
          />
        )}
      </td>
      <td className="py-1 px-1 w-16">
        <Input type="number" inputMode="decimal" className="h-8 text-xs text-right tabular-nums"
                value={ln.qty}
                data-testid={`gst-composer-line-qty-${i}`}
                onKeyDown={onLineKey}
                onChange={onQty} />
      </td>
      <td className="py-1 px-1 w-20">
        <Input type="number" inputMode="decimal" className="h-8 text-xs text-right tabular-nums"
                value={ln.rate}
                data-testid={`gst-composer-line-rate-${i}`}
                onKeyDown={onLineKey}
                onChange={onRate} />
      </td>
      <td className="py-1 px-1 w-14">
        <Input type="number" inputMode="decimal" className="h-8 text-xs text-right tabular-nums"
                value={ln.discount_pct}
                onKeyDown={onLineKey}
                onChange={onDisc} />
      </td>
      {gstOn && (
        <td className="py-1 px-1 w-14">
          <Input type="number" inputMode="decimal" className="h-8 text-xs text-right tabular-nums"
                  value={ln.gst_rate}
                  data-testid={`gst-composer-line-gst-${i}`}
                  onKeyDown={onLineKey}
                  onChange={onGst} />
        </td>
      )}
      <td className="py-1 px-1 text-right text-xs tabular-nums font-mono w-24">
        {amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
      </td>
      <td className="py-1 px-1 w-8 text-center">
        {canRemove && (
          <button
            type="button"
            onClick={onRm}
            className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-rose-50 dark:hover:bg-rose-950/40 text-rose-600 transition-colors"
            data-testid={`gst-composer-line-remove-${i}`}
            title="Remove"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
});

// Mobile stacked variant — exposes the same test IDs as the table row
// so Playwright/Testing agent flows don't care about viewport width.
const StackedRow = memo(function StackedRow({
  ln, i, gstOn, hsn, setLine, rmLine, pickHsn, canRemove, isLast, onEnterLast, ws, pickItem,
}) {
  const [richOpenStacked, setRichOpenStacked] = useState(false);
  const onDesc = useCallback((e) => setLine(i, { description: e.target.value }), [i, setLine]);
  const onRichSave = useCallback((val) => setLine(i, { description: val }), [i, setLine]);
  const onQty  = useCallback((e) => setLine(i, { qty: e.target.value }), [i, setLine]);
  const onRate = useCallback((e) => setLine(i, { rate: e.target.value }), [i, setLine]);
  const onDisc = useCallback((e) => setLine(i, { discount_pct: e.target.value }), [i, setLine]);
  const onGst  = useCallback((e) => setLine(i, { gst_rate: e.target.value }), [i, setLine]);
  const onHsn  = useCallback((v) => pickHsn(i, v), [i, pickHsn]);
  const onRm   = useCallback(() => rmLine(i), [i, rmLine]);
  const onPickItem = useCallback((item) => pickItem?.(i, item), [i, pickItem]);
  const onLineKey = useCallback((e) => {
    // Feb 2026 R293zzc · Description is now a textarea — plain Enter
    // must insert a newline (multiline support). Only Ctrl/Cmd+Enter
    // triggers "advance to next line" on the last row.
    if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return;
    if (!isLast) return;
    e.preventDefault();
    onEnterLast?.();
  }, [isLast, onEnterLast]);

  return (
    <div className="border border-border rounded-lg p-3 space-y-2" data-testid={`gst-composer-line-${i}`}>
      <div className={gstOn ? "grid grid-cols-1 sm:grid-cols-2 gap-2" : ""}>
        {gstOn && (
          <Select value={ln.hsn_sac || ""} onValueChange={onHsn}>
            <SelectTrigger className="h-10" data-testid={`gst-composer-line-hsn-${i}`}>
              <SelectValue placeholder="HSN / SAC" />
            </SelectTrigger>
            <SelectContent>
              {hsn.map((h) => (
                <SelectItem key={h.code} value={h.code}>
                  {h.code} · {h.description} · {h.gst_rate}%
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <AutoGrowTextarea
          testid={`gst-composer-line-desc-${i}`}
          placeholder="Description *"
          value={ln.description}
          onChange={onDesc}
          onKeyDown={onLineKey}
          max={10}
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setRichOpenStacked(true)}
          className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          data-testid={`gst-composer-line-desc-expand-${i}`}
        >
          <Maximize2 className="h-3.5 w-3.5" /> Rich text editor
        </button>
        {ws && pickItem && (
          <InlineCatalogPicker ws={ws} onPick={onPickItem} size="lg" />
        )}
      </div>
      {richOpenStacked && (
        <LineRichTextDialog
          value={ln.description || ""}
          onSave={(v) => { onRichSave(v); setRichOpenStacked(false); }}
          onClose={() => setRichOpenStacked(false)}
        />
      )}
      <div className={"grid gap-2 " + (gstOn ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-3")}>
        <div>
          <Label className="text-[11px] text-muted-foreground">Qty</Label>
          <Input type="number" inputMode="decimal" className="h-11" value={ln.qty}
                  data-testid={`gst-composer-line-qty-${i}`}
                  onKeyDown={onLineKey}
                  onChange={onQty} />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">Rate ₹</Label>
          <Input type="number" inputMode="decimal" className="h-11" value={ln.rate}
                  data-testid={`gst-composer-line-rate-${i}`}
                  onKeyDown={onLineKey}
                  onChange={onRate} />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">Disc %</Label>
          <Input type="number" inputMode="decimal" className="h-11" value={ln.discount_pct}
                  onKeyDown={onLineKey}
                  onChange={onDisc} />
        </div>
        {gstOn && (
          <div>
            <Label className="text-[11px] text-muted-foreground">GST %</Label>
            <Input type="number" inputMode="decimal" className="h-11" value={ln.gst_rate}
                    data-testid={`gst-composer-line-gst-${i}`}
                    onKeyDown={onLineKey}
                    onChange={onGst} />
          </div>
        )}
      </div>
      {canRemove && (
        <div className="flex justify-end">
          <Button size="sm" variant="ghost" className="h-8 text-xs text-rose-600"
                    data-testid={`gst-composer-line-remove-${i}`}
                    onClick={onRm}>
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
          </Button>
        </div>
      )}
    </div>
  );
});

function LineItemGrid({ lines, setLine, addLine, rmLine, pickHsn, hsn, gstOn, onOpenCatalog, ws, pickItem }) {
  const onEnterLast = useCallback(() => {
    addLine();
    setTimeout(() => {
      const rows = document.querySelectorAll('[data-testid^="gst-composer-line-desc-"]');
      if (rows.length) rows[rows.length - 1].focus();
    }, 0);
  }, [addLine]);

  const totalAmount = lines.reduce((s, l) => s + _lineAmount(l), 0);

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Line items</Label>
        <div className="flex items-center gap-1.5">
          {onOpenCatalog && (
            <Button size="sm" variant="outline" className="h-9"
                    data-testid="gst-composer-open-catalog" onClick={onOpenCatalog}>
              <Package className="h-4 w-4 mr-1" /> From catalog
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-9"
                    data-testid="gst-composer-add-line" onClick={addLine}>
            <Plus className="h-4 w-4 mr-1" /> Add line
          </Button>
        </div>
      </div>

      {/* Tabular view (sm+ breakpoints) */}
      <div className="hidden sm:block border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="gst-composer-lines-table">
            <thead className="bg-muted/60">
              <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                <th className="py-1.5 px-1 text-center w-7">#</th>
                {gstOn && <th className="py-1.5 px-1 w-24">HSN</th>}
                {/* Feb 2026 R293zzc · Description claims the remaining
                    width; qty/rate/disc/gst/amount shrink to the
                    minimum readable text width so long descriptions
                    get the real estate they need. */}
                <th className="py-1.5 px-1 w-auto min-w-[280px]">Description</th>
                <th className="py-1.5 px-1 text-right w-16">Qty</th>
                <th className="py-1.5 px-1 text-right w-20">Rate</th>
                <th className="py-1.5 px-1 text-right w-14">Disc%</th>
                {gstOn && <th className="py-1.5 px-1 text-right w-14">GST%</th>}
                <th className="py-1.5 px-1 text-right w-24">Amount</th>
                <th className="py-1.5 px-1 w-8"></th>
              </tr>
            </thead>
            <tbody className="bg-background">
              {lines.map((ln, i) => (
                <TableRow
                  key={i}
                  ln={ln}
                  i={i}
                  gstOn={gstOn}
                  hsn={hsn}
                  setLine={setLine}
                  rmLine={rmLine}
                  pickHsn={pickHsn}
                  canRemove={lines.length > 1}
                  isLast={i === lines.length - 1}
                  onEnterLast={onEnterLast}
                  ws={ws}
                  pickItem={pickItem}
                />
              ))}
            </tbody>
            <tfoot className="bg-muted/40 border-t border-border">
              <tr className="text-[11px] font-mono tabular-nums">
                <td colSpan={gstOn ? 6 : 5} className="py-1.5 px-2 text-right uppercase tracking-wide text-muted-foreground">
                  Subtotal (before tax)
                </td>
                {gstOn && <td></td>}
                <td className="py-1.5 px-1 text-right font-semibold">
                  {totalAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Mobile stacked view */}
      <div className="space-y-2 sm:hidden">
        {lines.map((ln, i) => (
          <StackedRow
            key={i}
            ln={ln}
            i={i}
            gstOn={gstOn}
            hsn={hsn}
            setLine={setLine}
            rmLine={rmLine}
            pickHsn={pickHsn}
            canRemove={lines.length > 1}
            isLast={i === lines.length - 1}
            onEnterLast={onEnterLast}
            ws={ws}
            pickItem={pickItem}
          />
        ))}
      </div>

      <div className="text-[10px] text-muted-foreground">
        ↵ Enter on the last row adds a line · ⌘/Ctrl+Enter saves
      </div>
    </section>
  );
}

export default memo(LineItemGrid);
